import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ProgressBar } from '@/components/SurveyComponents';
import { Step1TripType } from '@/pages/Step1';
import { Step2Group } from '@/pages/Step2';
import { Step3Logistics } from '@/pages/Step3';
import { Step4Budget } from '@/pages/Step4';
import { Step5DeepDive } from '@/pages/Step5';
import { Step6Dining } from '@/pages/Step6';
import { Step7Final } from '@/pages/Step7';
import { initialData, STEPS } from '@/lib/index';
import type { SurveyData, TripType } from '@/lib/index';
import { submitSurveyEmail } from '@/lib/emailSubmit';

const TOTAL_STEPS = STEPS.length;

const slideVariants = {
  enter: (dir: number) => ({ x: dir > 0 ? 60 : -60, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir: number) => ({ x: dir > 0 ? -60 : 60, opacity: 0 }),
};

export function Home() {
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [data, setData] = useState<SurveyData>(initialData);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const update = (partial: Partial<SurveyData>) => setData(prev => ({ ...prev, ...partial }));

  const goNext = () => {
    if (step < TOTAL_STEPS) {
      setDirection(1);
      setStep(s => s + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      handleSubmit();
    }
  };

  const goPrev = () => {
    if (step > 1) {
      setDirection(-1);
      setStep(s => s - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(false);
    try {
      await submitSurveyEmail(data);
      setSubmitted(true);
    } catch (err) {
      console.error('[MbM Survey] Submission error:', err);
      setSubmitError(true);
    } finally {
      setSubmitting(false);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const canAdvance = (): boolean => {
    if (step === 1) return !!data.tripType;
    if (step === 2) return !!(data.name && data.email && data.groupSize && data.groupRelationship && data.occasion);
    if (step === 3) return !!(data.preferredDates && data.duration && data.hasDestination && data.internationalOk);
    if (step === 4) return !!(data.budgetPerPerson && data.overallVibe);
    return true;
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4 py-16 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="max-w-lg w-full space-y-6"
        >
          <div className="text-6xl">✈️</div>
          <h1 className="font-serif text-4xl font-bold text-foreground">You are officially on my radar.</h1>
          <p className="text-muted-foreground text-base leading-relaxed">
            Thank you for filling out the Mapped by Mattie survey. I read every single answer with care and will be in touch within 48 hours to start building your dream itinerary.
          </p>
          <p className="text-secondary font-semibold font-serif text-lg">Plan Bold. Travel Better.</p>
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <span>Follow along</span>
            <a href="https://instagram.com/MappedByMattie" target="_blank" rel="noreferrer"
               className="text-secondary font-medium hover:underline">@MappedByMattie</a>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/90 backdrop-blur border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-serif text-xl font-bold text-foreground tracking-tight">Mapped by Mattie</h1>
            <p className="text-[10px] text-muted-foreground font-medium tracking-widest uppercase">Plan Bold. Travel Better.</p>
          </div>
          <span className="text-xs text-muted-foreground font-medium">{step}/{TOTAL_STEPS}</span>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="max-w-2xl mx-auto px-4 pt-6">
        <ProgressBar currentStep={step} />
      </div>

      {/* Step Content */}
      <div className="max-w-2xl mx-auto px-4 pb-32">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={step}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.35, ease: 'easeInOut' }}
          >
            {step === 1 && <Step1TripType tripType={data.tripType} onChange={(t: TripType) => update({ tripType: t })} />}
            {step === 2 && <Step2Group data={data} update={update} />}
            {step === 3 && <Step3Logistics data={data} update={update} />}
            {step === 4 && <Step4Budget data={data} update={update} />}
            {step === 5 && <Step5DeepDive data={data} update={update} />}
            {step === 6 && <Step6Dining data={data} update={update} />}
            {step === 7 && <Step7Final data={data} update={update} />}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation Footer */}
      <div className="fixed bottom-0 left-0 right-0 z-20 bg-background/95 backdrop-blur border-t border-border">
        {submitError && (
          <div className="max-w-2xl mx-auto px-4 pt-3">
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2 text-center">
              Something went wrong sending your survey. Please try again or email us directly at mattiesmith46@mappedbymattie.com
            </p>
          </div>
        )}
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          {step > 1 ? (
            <button type="button" onClick={goPrev} disabled={submitting}
              className="px-6 py-3 rounded-xl border-2 border-border text-sm font-medium text-foreground hover:bg-muted transition-colors duration-200 disabled:opacity-50">
              Back
            </button>
          ) : (
            <div />
          )}
          <button type="button" onClick={goNext} disabled={!canAdvance() || submitting}
            className={`flex-1 sm:flex-none sm:min-w-[160px] px-8 py-3 rounded-xl text-sm font-semibold transition-all duration-200 ${
              canAdvance() && !submitting
                ? 'bg-secondary text-secondary-foreground hover:opacity-90 shadow-md'
                : 'bg-muted text-muted-foreground cursor-not-allowed'
            }`}>
            {submitting ? 'Sending...' : step === TOTAL_STEPS ? 'Submit My Trip 🌍' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
